/**
 * CUSTOM (HOMYCASA): color swatches inside Doofinder result cards.
 *
 * The Doofinder layer builds its cards from the index feed, which has no access to
 * `variant.metafields.variante.cor` — a variant-level metaobject reference holding the
 * hex list and the texture image — so the swatches cannot be drawn from the card
 * template itself. The template only prints an empty slot (see
 * snippets/doofinder-template.liquid) and this script fills it with the exact markup
 * the theme renders for a grid card, fetched from
 * `/products/<handle>?section_id=section-rendering-product-card`.
 *
 * The injected swatches are decorative. The radio inputs stay in the DOM so the
 * available/checked styling in base-homycasa.css keeps working, but they are taken out
 * of the tab order and the slot is `pointer-events: none`, so a click anywhere on the
 * card still follows Doofinder's own navigation and tracking.
 */

const SLOT_SELECTOR = '[data-hc-swatches]';
const SECTION_ID = 'section-rendering-product-card';
const MAX_CONCURRENT_REQUESTS = 4;

/** @type {Map<string, Promise<string | null>>} Product path -> swatch markup (or null when the product has none). */
const markupCache = new Map();

/** @type {Array<{ run: () => Promise<string | null>, resolve: (value: string | null) => void }>} */
const pending = [];
let inFlight = 0;

/**
 * Runs queued requests at most MAX_CONCURRENT_REQUESTS at a time, so a full page of
 * results does not fire dozens of section requests at once.
 * @param {() => Promise<string | null>} run
 * @returns {Promise<string | null>}
 */
function enqueue(run) {
  return new Promise((resolve) => {
    pending.push({ run, resolve });
    drain();
  });
}

function drain() {
  while (inFlight < MAX_CONCURRENT_REQUESTS && pending.length > 0) {
    const job = pending.shift();
    if (!job) return;

    inFlight += 1;
    job
      .run()
      .catch(() => null)
      .then((value) => job.resolve(value))
      .finally(() => {
        inFlight -= 1;
        drain();
      });
  }
}

/**
 * Turns a Doofinder item link into a same-origin product section URL.
 * Query params are dropped so every card for a product shares one cache entry.
 * @param {string | null | undefined} link
 * @returns {URL | null}
 */
function sectionUrl(link) {
  if (!link) return null;

  try {
    const source = new URL(link, window.location.origin);
    if (source.origin !== window.location.origin) return null;
    if (!source.pathname.includes('/products/')) return null;

    const url = new URL(source.pathname, window.location.origin);
    url.searchParams.set('section_id', SECTION_ID);
    return url;
  } catch {
    return null;
  }
}

/**
 * Pulls the swatch fieldset out of a rendered product card section and strips
 * everything that would make it interactive.
 * @param {string} html
 * @returns {string | null}
 */
function extractSwatches(html) {
  const fieldset = new DOMParser()
    .parseFromString(html, 'text/html')
    .querySelector('swatches-variant-picker-component .variant-option--swatches');

  if (!fieldset) return null;

  fieldset.querySelectorAll('script').forEach((node) => node.remove());

  for (const node of fieldset.querySelectorAll('*')) {
    for (const { name } of [...node.attributes]) {
      // `on:*` are the theme's declarative event bindings.
      if (name.startsWith('on')) node.removeAttribute(name);
    }
  }

  const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (fieldset.querySelectorAll('input'));
  inputs.forEach((input) => input.setAttribute('tabindex', '-1'));

  // The grid card marks the first swatch; the section renders with
  // `has_option_selected: true` and no selected variant, so nothing comes back checked.
  if (inputs.length > 0 && ![...inputs].some((input) => input.hasAttribute('checked'))) {
    inputs[0].setAttribute('checked', '');
  }

  fieldset.querySelectorAll('a').forEach((anchor) => {
    anchor.removeAttribute('href');
    anchor.setAttribute('tabindex', '-1');
    anchor.setAttribute('aria-hidden', 'true');
  });

  return fieldset.outerHTML;
}

/**
 * @param {URL} url
 * @returns {Promise<string | null>}
 */
function loadSwatches(url) {
  const cached = markupCache.get(url.pathname);
  if (cached) return cached;

  const request = enqueue(async () => {
    const response = await fetch(url.href);
    if (!response.ok) return null;
    return extractSwatches(await response.text());
  });

  markupCache.set(url.pathname, request);
  return request;
}

/**
 * @param {HTMLElement} slot
 */
async function hydrate(slot) {
  if (slot.dataset.hcSwatchesState) return;
  slot.dataset.hcSwatchesState = 'loading';

  const url = sectionUrl(slot.dataset.hcSwatchesLink);
  if (!url) {
    slot.dataset.hcSwatchesState = 'empty';
    return;
  }

  const markup = await loadSwatches(url);

  // Doofinder re-renders its results on every keystroke; the slot may be gone by now.
  if (!slot.isConnected) return;

  if (!markup) {
    slot.dataset.hcSwatchesState = 'empty';
    return;
  }

  slot.innerHTML = `<product-swatches>${markup}</product-swatches>`;
  slot.dataset.hcSwatchesState = 'ready';
}

const visibility =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            observer.unobserve(entry.target);
            hydrate(/** @type {HTMLElement} */ (entry.target));
          }
        },
        { rootMargin: '300px' }
      )
    : null;

/**
 * @param {ParentNode} root
 */
function collect(root) {
  for (const slot of /** @type {NodeListOf<HTMLElement>} */ (root.querySelectorAll(SLOT_SELECTOR))) {
    if (slot.dataset.hcSwatchesState) continue;
    if (visibility) visibility.observe(slot);
    else hydrate(slot);
  }
}

function watch() {
  let scheduled = false;

  // Doofinder re-renders results as the query changes, and a re-render can either add
  // slots or morph existing ones back to empty. Rescanning the whole document on the
  // next frame covers both; already-hydrated slots are skipped and repeat products are
  // served from markupCache, so no extra requests are made.
  const rescan = () => {
    scheduled = false;
    collect(document);
  };

  collect(document);

  new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(rescan);
  }).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watch, { once: true });
} else {
  watch();
}
